/**
 * FormSpec — the editable shape the Forms Builder authors, either conversationally
 * via the AI agent (`formBuilderAgent`) or by hand on the drag-drop canvas.
 *
 * Deliberately mirrors `workflow-spec.ts`:
 *   - A `FormSpec` lives inside a `form_drafts` row while being built.
 *   - On "Publish" it snapshots into a `forms` row with an incrementing version.
 *   - A `form_submissions` row records the captured `values` (keyed by field.key)
 *     plus its context binding; those values become the "dimensions" a workflow
 *     reads to drive routing (see services/workflows/dimensions.ts, P2).
 *
 * No `zod` in this project, so `validateFormSpec` is hand-rolled and defensive:
 * every AI tool output and manual edit passes through it, so it MUST never throw.
 *
 * Layout is a flat `fields` pool referenced by id from `sections`, referenced by
 * id from `views`. Views double as wizard pages. A `repeatable` section captures
 * an array of row objects (table-style). Fields can be conditionally shown via
 * `visibleWhen`. A `calculated` field derives its value from a `formula` over
 * other field keys.
 */

import type { WorkflowContextType, WorkflowContextRef } from '@/types/workflow-spec'
import { normalizeAccessRules, type FolderTemplate } from '@/services/documents/libraryTypes'
import { parseUtcDate } from '@/lib/utcDate'

// ---------------------------------------------------------------------------
// Field types
// ---------------------------------------------------------------------------

export type FormFieldType =
  | 'short_text'
  | 'long_text'
  | 'rich_text'
  | 'number'
  | 'currency'
  | 'percent'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'single_select'
  | 'multi_select'
  | 'email'
  | 'phone'
  | 'url'
  | 'file'
  | 'signature'
  | 'section_header'
  | 'lookup'
  | 'calculated'
  | 'sap_lookup'
  | 'people'
  | 'document_repository'
  | 'document_reference'

export const FORM_FIELD_TYPES: FormFieldType[] = [
  'short_text', 'long_text', 'rich_text', 'number', 'currency', 'percent',
  'date', 'datetime', 'boolean', 'single_select', 'multi_select', 'email',
  'phone', 'url', 'file', 'signature', 'section_header', 'lookup', 'calculated',
  'sap_lookup', 'people', 'document_repository', 'document_reference',
]

/**
 * Field types that capture no submittable value: a heading, and a document
 * repository (a document library shown on the page: its documents live in the
 * library, never in the submission's values). Everything that reads values
 * goes through isValueField, so these stay out of required checks, routing
 * dimensions, exports, bulk rows and the data-standard checks.
 */
export const PRESENTATIONAL_FIELD_TYPES: FormFieldType[] = ['section_header', 'document_repository']

export interface FormFieldOption {
  id: string
  label: string
  value: string
  /** Set only on options resolved from a shared list (never stored on a spec):
   *  the value is retired, so it shows where already saved but cannot be picked. */
  retired?: boolean
  /** Cascading selects: the parent field's stored values this option belongs
   *  to (the field names its parent in `dependsOn`). Absent or empty = offered
   *  whatever the parent holds. Inline options store it on the spec; shared
   *  list items carry it in the list and it is copied on resolution. */
  parentValues?: string[]
}

export type VisibilityOp =
  | 'eq' | 'neq' | 'in' | 'not_in' | 'gt' | 'gte' | 'lt' | 'lte' | 'empty' | 'not_empty' | 'contains'

export const VISIBILITY_OPS: VisibilityOp[] = [
  'eq', 'neq', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'empty', 'not_empty', 'contains',
]

/** A single show/hide condition referencing another field by its machine key. */
export interface VisibilityRule {
  fieldKey: string
  op: VisibilityOp
  value?: unknown
}

/** all = every rule must pass (AND); any = at least one (OR). Both may be set. */
export interface VisibilityGuard {
  all?: VisibilityRule[]
  any?: VisibilityRule[]
}

/** Bounds use UTC calendar days. Every matching rule applies (intersection).
 * An array is AND, or use the visibleWhen-style { all?, any? } guard. */
export interface ConditionalDateRule {
  when: VisibilityRule[] | VisibilityGuard
  minDate?: string
  maxDate?: string
  message?: string
}

/** Restrict a select's choices while `when` matches ("Change Type is BOM
 * limits Change Code to these"). Every matching rule applies (intersection),
 * after any `dependsOn` filtering. `values` are stored option values. */
export interface AllowedOptionsRule {
  when: VisibilityRule[] | VisibilityGuard
  values: string[]
}

/**
 * Governed request numbers (docs/NUMBERING.md). Tokens: {seq:N} (exactly one,
 * N = 1..12 digits, zero padded), {YYYY}, {YY} (UTC year of the submission)
 * and {site} (code of the request's site). The server allocates the number when
 * the request is submitted (migration 00532). `sitePatterns` overrides the
 * pattern per site code. Blank pattern = numbering off (short reference).
 */
export interface FormNumberingConfig {
  pattern?: string
  sitePatterns?: Record<string, string>
}

export type FormFieldWidth = 'full' | 'half' | 'third'

// ---------------------------------------------------------------------------
// Field data standards (plain-language rules compiled to deterministic checks)
// ---------------------------------------------------------------------------

/** Character-class constraint names for `charset` rules. */
export type StandardCharset = 'numeric' | 'alpha' | 'alphanumeric' | 'uppercase'
export const STANDARD_CHARSETS: StandardCharset[] = ['numeric', 'alpha', 'alphanumeric', 'uppercase']

export type StandardRuleKind = 'prefix' | 'suffix' | 'length' | 'charset' | 'regex' | 'in_list'
export const STANDARD_RULE_KINDS: StandardRuleKind[] = ['prefix', 'suffix', 'length', 'charset', 'regex', 'in_list']

/**
 * One deterministic, compiled data-standard rule. The author writes the
 * standard in plain language; an AI Compile step emits these; RUNTIME
 * enforcement is pure evaluation (no AI). Extensible by adding kinds.
 *
 *  - prefix / suffix: `value` must start/end with `value`.
 *  - length: min / max / exact character counts (any subset).
 *  - charset: the whole value matches the named character class.
 *  - regex: `pattern` with a human `message` (message required at compile).
 *  - in_list: the value must be one of `values`.
 *
 * `when` makes a rule conditional: it applies only when EVERY listed
 * VisibilityRule passes against the live form values (AND), reusing the same
 * predicate model as visibleWhen/requiredWhen. So "if company code is 1234 the
 * first 4 characters must be 1000" compiles to a prefix rule with a when.
 */
export interface CompiledStandardRule {
  kind: StandardRuleKind
  /** prefix / suffix */
  value?: string
  /** length */
  min?: number
  max?: number
  exact?: number
  /** charset */
  charset?: StandardCharset
  /** regex */
  pattern?: string
  /** in_list */
  values?: string[]
  /** Human violation message. Falls back to a generated sentence per kind. */
  message?: string
  /** Conditional wrapper: apply only when ALL of these pass (AND-ed). */
  when?: VisibilityRule[]
}

/** A field's data standard: the author's plain-language text plus the
 *  deterministic rules it compiled to. `uninterpretable` keeps every statement
 *  the compiler could not turn into a rule, shown to the author and never
 *  silently dropped. `compiledFrom` is the exact text the rules came from, so
 *  the builder can flag a stale compile after the text is edited. */
export interface FieldDataStandard {
  text: string
  rules: CompiledStandardRule[]
  uninterpretable?: string[]
  compiledAt: string
  compiledFrom?: string
}

// ---------------------------------------------------------------------------
// Document repository element
// ---------------------------------------------------------------------------

/** 'library': the library (or one branch of it) as is, the same for everyone
 *  who opens the form. 'request': each request gets its own folder. */
export type RepositoryScope = 'library' | 'request'
export const REPOSITORY_SCOPES: RepositoryScope[] = ['library', 'request']

/** Settings of a `document_repository` field. */
export interface FormRepositoryConfig {
  /** doc_libraries.id, or null until the designer has chosen a library. */
  libraryId: string | null
  scope: RepositoryScope
  /** Folder under the library root, "/" between levels. Absent = the root.
   *  For 'library' it is the branch shown; for 'request' it is where the
   *  per-request folders are created. */
  folderPath?: string
  /** A library the designer is laying out but has not created yet. Cleared
   *  (and libraryId set) once it is created. */
  newLibrary?: { name: string; folders: FolderTemplate[] } | null
}

/** Longest library name / folder name kept in a repository block. */
const REPOSITORY_NAME_MAX = 120
/** Deepest starter folder structure kept, and the most folders per level. */
const REPOSITORY_TREE_MAX_DEPTH = 8
const REPOSITORY_TREE_MAX_SIBLINGS = 200

/** "a / b//c/" -> "a/b/c". Never throws; anything that is not text reads as ''. */
export function normalizeFolderPath(v: unknown): string {
  if (typeof v !== 'string') return ''
  return v.split('/').map((p) => p.trim()).filter(Boolean).join('/')
}

function normalizeFolderTemplates(raw: unknown, depth = 0): FolderTemplate[] {
  if (!Array.isArray(raw) || depth >= REPOSITORY_TREE_MAX_DEPTH) return []
  const out: FolderTemplate[] = []
  for (const r of raw.slice(0, REPOSITORY_TREE_MAX_SIBLINGS)) {
    if (!r || typeof r !== 'object' || Array.isArray(r)) continue
    const o = r as Record<string, unknown>
    // A blank name is kept: it is a folder the designer has added and not
    // named yet. Creating the library skips it.
    const name = (typeof o.name === 'string' ? o.name : '').slice(0, REPOSITORY_NAME_MAX)
    const permissions = normalizeAccessRules(o.permissions)
    const children = normalizeFolderTemplates(o.children, depth + 1)
    out.push({
      name,
      ...(permissions.length > 0 ? { permissions } : {}),
      ...(children.length > 0 ? { children } : {}),
    })
  }
  return out
}

/**
 * Normalize a repository block defensively. Always returns a usable block (no
 * library, shared scope) so a `document_repository` field never has to be
 * null-checked. Never throws.
 */
export function normalizeRepository(raw: unknown): FormRepositoryConfig {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const libraryId = typeof r.libraryId === 'string' && r.libraryId.trim() ? r.libraryId.trim() : null
  const folderPath = normalizeFolderPath(r.folderPath)
  const out: FormRepositoryConfig = {
    libraryId,
    scope: r.scope === 'request' ? 'request' : 'library',
    ...(folderPath ? { folderPath } : {}),
  }
  // A library that is still being laid out only matters while none is chosen.
  const draft = r.newLibrary
  if (!libraryId && draft && typeof draft === 'object' && !Array.isArray(draft)) {
    const n = draft as Record<string, unknown>
    out.newLibrary = {
      name: (typeof n.name === 'string' ? n.name : '').slice(0, REPOSITORY_NAME_MAX),
      folders: normalizeFolderTemplates(n.folders),
    }
  }
  return out
}

/** The repository settings of a field, or null when it is not a repository. */
export function repositoryOf(field: Pick<FormField, 'type' | 'repository'>): FormRepositoryConfig | null {
  return field.type === 'document_repository' ? normalizeRepository(field.repository) : null
}

/** The document repository elements of a form that are shown (not archived). */
export function repositoryFields(spec: Pick<FormSpec, 'fields'>): FormField[] {
  return (spec.fields ?? []).filter((f) => f.type === 'document_repository' && !f.archived)
}

/**
 * A form field. Flat by design (like WorkflowStep): common props plus optional
 * per-type validation/config props. Consumers switch on `type` and read the
 * relevant optional props. `key` is the stable machine name — the dimension a
 * workflow condition reads (`form.<key>`), so it must stay stable across edits.
 */
export interface FormField {
  id: string
  type: FormFieldType
  key: string
  label: string
  helpText?: string
  placeholder?: string
  required?: boolean
  /** Archived: kept for historical viewing of past submissions but hidden on new
   *  forms (not rendered, not validated). Unarchive to show it again. */
  archived?: boolean
  defaultValue?: unknown
  width?: FormFieldWidth
  /** Show this field only when the guard passes (references other fields by key). */
  visibleWhen?: VisibilityGuard
  /** Make this field required only when the guard passes (conditional enforce).
   *  Effective-required = required || requiredWhen matches. Evaluated against the
   *  live values, same predicate model as visibleWhen. */
  requiredWhen?: VisibilityGuard
  /** Field-level workflow lock. When a submission is awaiting *others'* approval
   *  the submitter may only change fields with this flag on (everything else is
   *  read-only until the run returns to them). Default false = locked in flight. */
  editableInWorkflow?: boolean

  // text (short_text / long_text / rich_text)
  minLength?: number
  maxLength?: number
  regex?: string
  regexMessage?: string
  rows?: number

  /** Data standard: plain-language rules compiled (via AI, at design time) into
   *  deterministic checks enforced by fieldValidation.ts at runtime. */
  dataStandard?: FieldDataStandard

  // number / currency / percent
  min?: number
  max?: number
  step?: number
  integerOnly?: boolean
  currencyCode?: string
  decimals?: number

  // date / datetime (ISO string, today, or today+Nd / today-Nd; UTC)
  /** Server-recorded date of this request's first entry into its approval workflow.
   * Blank before routing, read-only, and unchanged by returns or resubmissions. */
  dateSource?: 'first_workflow_submission'
  minDate?: string
  maxDate?: string
  conditionalDateRules?: ConditionalDateRule[]

  // boolean
  trueLabel?: string
  falseLabel?: string

  // single_select / multi_select
  options?: FormFieldOption[]
  /** Options source: when set, the field takes its options from this shared
   *  select list (form_option_lists.id, services/forms/optionLists.ts), read at
   *  render time, and `options` is kept untouched for switching back. An empty
   *  string means "Shared list" is chosen but no list is picked yet. */
  optionListId?: string
  allowOther?: boolean
  /** Cascading select: the key of the select field whose value filters this
   *  field's options (by each option's parentValues). Changing the parent
   *  clears a choice it no longer allows. */
  dependsOn?: string
  /** Restrict the options by other answers. See AllowedOptionsRule. */
  allowedOptionsWhen?: AllowedOptionsRule[]
  minSelected?: number
  maxSelected?: number

  // url / phone
  allowedSchemes?: string[]
  country?: string

  // file
  maxFiles?: number
  maxSizeMb?: number
  acceptedTypes?: string[]

  // signature
  requireTypedName?: boolean

  // section_header
  subtitle?: string

  // lookup (reference to a PPM entity via contextRegistry)
  entity?: WorkflowContextType
  multiple?: boolean

  // calculated (arithmetic formula over other field keys, e.g. "qty * price")
  formula?: string

  // SAP DDIC identity: which SAP data-dictionary field this value lands in
  // ('CSKS-KOSTL'). Resolved against services/sap/sapDdicCatalog.ts; drives the
  // SAP object-info chip on the renderer and the deterministic DDIC validation
  // (length, NUMC digits) on free-entry text fields.
  sapDdicRef?: string

  // sap_lookup (Connect to SAP): which curated SAP object to pull from, and an
  // optional scope (e.g. the ProjectID whose WBS elements to offer).
  sapSource?: string
  sapProjectId?: string
  /** Filters that narrow a sap_lookup search (filter key -> value), keyed by the
   *  source's filter catalog (e.g. { companyCode: '1710' }). */
  sapFilters?: Record<string, string>
  /** For a scoped sap_lookup source (e.g. material, scoped by plant): the key of
   *  the field on this form whose LIVE value scopes this lookup. The material
   *  list is then restricted to that plant, and make vs buy is read plant-scoped. */
  sapScopeFieldKey?: string

  // Linked attribute: auto-populate this field (read-only) from an attribute of
  // the record picked in another sap_lookup field. `sourceFieldKey` is that
  // field's key; `sourceAttribute` is an attribute id from its SAP source.
  sourceFieldKey?: string
  sourceAttribute?: string

  // document_repository: which document library the element shows, and how.
  repository?: FormRepositoryConfig

  // people: where the choices come from. 'staff' (default) is the staff
  // directory and People Groups; 'directory' searches the connected Entra
  // directory live (every employee, no import first) and stores a
  // DirectoryPersonRef { kind, oid, name, email, userId }.
  peopleSource?: PeopleSource

  /** Prefill this field on a new form from the signed-in requester's profile
   *  (refreshed from the Entra directory). Only fills a blank value. */
  prefillFrom?: RequesterAttribute
  // document_reference: which controlled documents can be picked (the value is
  // DocumentReference[]: document id plus the revision at the time of the pick).
  documentRef?: FormDocumentRefConfig
}

/** Settings of a `document_reference` field. */
export interface FormDocumentRefConfig {
  /** Libraries to pick from; empty = every library the person can open. */
  libraryIds?: string[]
  /** Offer only documents in a released status (Approved). */
  releasedOnly?: boolean
  /** Allow more than one document. */
  multiple?: boolean
}

/** Normalize a document reference block. Never throws. */
export function normalizeDocumentRef(raw: unknown): FormDocumentRefConfig {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const ids = Array.isArray(r.libraryIds) ? [...new Set(r.libraryIds.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim()))] : []
  return {
    ...(ids.length > 0 ? { libraryIds: ids } : {}),
    ...(r.releasedOnly === true ? { releasedOnly: true } : {}),
    ...(r.multiple === true ? { multiple: true } : {}),
  }
}

export type PeopleSource = 'staff' | 'directory'

/** Requester profile attributes a field can prefill from and routing can read
 *  as `requester.<attribute>` (services/directory/personnel.ts). */
export type RequesterAttribute = 'name' | 'email' | 'jobTitle' | 'department' | 'officeLocation' | 'manager' | 'managerEmail'

export const REQUESTER_ATTRIBUTES: { value: RequesterAttribute; label: string }[] = [
  { value: 'name', label: 'Requester name' },
  { value: 'email', label: 'Requester email' },
  { value: 'jobTitle', label: 'Requester job title' },
  { value: 'department', label: 'Requester department' },
  { value: 'officeLocation', label: 'Requester office / site' },
  { value: 'manager', label: "Requester's manager" },
  { value: 'managerEmail', label: "Requester's manager email" },
]

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export type SectionColumns = 1 | 2 | 3

export interface FormSection {
  id: string
  title: string
  description?: string
  columns?: SectionColumns
  /** A repeatable section captures an array of row objects (table-style). */
  repeatable?: boolean
  minRows?: number
  maxRows?: number
  addRowLabel?: string
  /** Ordered field ids laid out in this section. */
  fieldIds: string[]
}

export interface FormView {
  id: string
  title: string
  description?: string
  /** Ordered section ids on this view. More than one view = wizard pages. */
  sectionIds: string[]
}

/** In-form approval: submitting the form starts an approval on the unified engine. */
export interface FormApprovalConfig {
  enabled: boolean
  approverRole?: string | null
  slaHours?: number | null
}

/**
 * Post-approval SAP Activity binding. After the form's hooked workflow is
 * approved, the submission can execute a curated SAP "activity" (create a
 * master-data object such as a Cost Center) via a manual Deploy step. The
 * activity catalog lives in services/sap/sapActivities.ts.
 */
export interface SapActivityBinding {
  enabled: boolean
  /** SapActivityDef.id from the curated catalog. */
  activityId: string
  /** Optional owning Solution/Architecture Studio workstream agent (metadata). */
  agentWorkstream?: string | null
  /** activity param key -> form field key that supplies it. Unmapped required
   *  params are gathered by an AI conversation at deploy time. */
  fieldMap?: Record<string, string>
}

/**
 * A "surface" a form can be attached to. Superset of WorkflowContextType with
 * the two extra app areas the forms extend into (Timesheet, Organizational).
 */
export type FormSurface = WorkflowContextType | 'timesheet' | 'organizational'

export const FORM_SURFACES: { value: FormSurface; label: string }[] = [
  { value: 'global', label: 'Global (anywhere)' },
  { value: 'project', label: 'Project' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'organizational', label: 'Organizational' },
  { value: 'risk', label: 'Risk' },
  { value: 'request', label: 'Project Request' },
  { value: 'bcr', label: 'Change Request' },
  { value: 'baseline', label: 'Baseline' },
  { value: 'forecast', label: 'Forecast' },
]

/**
 * Bulk (multi-object) capture. When enabled, ONE section of the form becomes the
 * per-row columns of an editable grid: the request carries N objects of the same
 * type, rides ONE approval cycle, and deploys per row.
 *
 * Storage reuses the EXISTING repeatable-section shape:
 *   values[bulk.sectionId] = Array<Record<fieldKey, value>>
 * so there is no second row model in the codebase. Every field NOT in that
 * section is a header field, captured once and shared by every row.
 */
export interface FormBulkConfig {
  enabled: boolean
  /** The one section whose fields become the per-row columns. */
  sectionId: string
  /** Row cap for the grid and the importer. Defaults to DEFAULT_BULK_MAX_ROWS. */
  maxRows?: number
  /** Row field whose value identifies the object (used for duplicate detection
   *  and the per-row deploy key). Falls back to the post-approval activity's
   *  objectKeyParam mapping, then the first required row field. */
  objectKeyFieldKey?: string
}

/**
 * How the public link (/f/<slug>) presents the form to people outside the app:
 * the owner's organization name and logo, an intro above the fields, the
 * message shown after submitting, and who to contact. Set on the builder's
 * Settings tab; read only by the public page (through readPublicPage in
 * modules/workflow-management/components/publicFormGuards.ts, which also
 * refuses an unsafe logo address or a malformed email). It changes nothing for
 * people who open the form signed in.
 */
export interface FormPublicPage {
  organizationName?: string
  logoUrl?: string
  introText?: string
  confirmationMessage?: string
  contactEmail?: string
}

/** Longest value kept for each public page setting. */
export const PUBLIC_PAGE_LIMITS: Record<keyof FormPublicPage, number> = {
  organizationName: 120,
  logoUrl: 200_000,
  introText: 2000,
  confirmationMessage: 1000,
  contactEmail: 254,
}

/** Default cap on rows in one bulk request. */
export const DEFAULT_BULK_MAX_ROWS = 200
/** Hard ceiling normalization clamps `maxRows` to. */
export const BULK_MAX_ROWS_CEILING = 1000

export interface FormSpec {
  name: string
  description?: string
  submitLabel?: string
  fields: FormField[]
  sections: FormSection[]
  views: FormView[]
  /** Multi-object capture (Single vs Multiple toggle on the renderer). */
  bulk?: FormBulkConfig
  /** Render the views as a sequential wizard (Next/Back) rather than tabs. */
  wizard?: boolean
  /** The surface a submission attaches to; default 'global'. */
  contextBinding?: FormSurface
  /** When bound to a surface, show the form on an entity only when its
   *  attributes satisfy this guard (e.g. project.status == 'Startup'). */
  surfaceVisibility?: VisibilityGuard
  approval?: FormApprovalConfig
  /** Hooked workflow (by its draft, which holds the spec) started on submit;
   *  its run progress is shown on the form so users see completed/pending steps. */
  workflowDraftId?: string | null
  /** After the hooked workflow is approved, run this SAP activity (MDG deploy). */
  postApproval?: SapActivityBinding
  /** Presentation of the public link only. Absent when nothing is set. */
  publicPage?: FormPublicPage
  /** The statuses a request moves through after it is submitted. Workflow
   *  steps and form owners or administrators set them; each can notify. */
  requestStatuses?: RequestStatus[]
  /** Request number pattern. Absent = requests show the short reference. */
  numbering?: FormNumberingConfig
  notes?: string
}

/** Chip colours a request status can take. */
export const REQUEST_STATUS_COLORS = ['slate', 'blue', 'amber', 'violet', 'green', 'red'] as const
export type RequestStatusColor = (typeof REQUEST_STATUS_COLORS)[number]

/** Who hears about it when a request enters a status. */
export interface RequestStatusNotify {
  /** The person who submitted the request (in-app bell, and email when on). */
  requester?: boolean
  /** Directory people (name or email) and `@Group` tokens. */
  recipients?: string[]
  /** Also email the requester and recipients (needs an email sender on the server). */
  email?: boolean
  /** Also message the requester and recipients in Teams (direct-message flow). */
  teamsDirect?: boolean
  /** Post to this Teams channel destination (name or id). */
  teamsChannel?: string
  /** Extra text for the message. `{{form.<key>}}` inserts a submitted value. */
  message?: string
}

/** One status in a form's request lifecycle. */
export interface RequestStatus {
  /** Stable machine key, kept when the label is renamed. */
  key: string
  label: string
  color: RequestStatusColor
  /** The status a new submission starts in. Exactly one per list. */
  initial?: boolean
  /** The request is finished in this status (shown as closed). */
  closed?: boolean
  notify?: RequestStatusNotify
}

/** A submission's current request status. */
export interface FormSubmissionRequestStatus {
  key: string
  at: string | null
  by: string | null
}

// ---------------------------------------------------------------------------
// Draft / published form / submission
// ---------------------------------------------------------------------------

export type FormDraftStatus = 'draft' | 'in_review' | 'published' | 'archived'

export interface FormChatMessage {
  role: 'user' | 'assistant'
  content: string
  /** One-line summary of the form change the assistant made on this turn. */
  toolSummary?: string
  ts: string
}

export interface FormChatHistory {
  messages: FormChatMessage[]
}

export interface FormDraft {
  id: string
  name: string
  status: FormDraftStatus
  portfolioId: string | null
  ownerId: string | null
  aiGenerated: boolean
  spec: FormSpec
  chatHistory: FormChatHistory
  /** forms.id once published (twin of WorkflowDraft.promotedWorkflowId). */
  publishedFormId: string | null
  createdAt: string
  updatedAt: string
}

/** A published, version-pinned snapshot of a FormSpec. */
export interface PublishedForm {
  id: string
  draftId: string | null
  name: string
  version: number
  status: 'published' | 'archived'
  portfolioId: string | null
  ownerId: string | null
  spec: FormSpec
  contextBinding: FormSurface | null
  /** Public share: a stable slug + a public toggle (link is /f/<slug>). */
  publicSlug: string | null
  isPublic: boolean
  createdAt: string
  updatedAt: string
}

export interface FormAttachmentRef {
  fieldKey: string
  /** Storage path within the form-attachments bucket. */
  path: string
  filename: string
  mime: string | null
  sizeBytes: number | null
}

/** SAP deploy lifecycle of a submission (post-approval MDG execution). */
export type FormDeployStatus = 'none' | 'pending' | 'deploying' | 'deployed' | 'failed' | 'handoff_pending'

export interface FormSubmissionDeploy {
  status: FormDeployStatus
  /** { ok, objectKey, messages } returned by the executor (or handoff note). */
  result?: unknown
  /** The finalized inputs sent to SAP (AI-gathered + form values). */
  payload?: Record<string, unknown> | null
  at?: string | null
  by?: string | null
}

export interface FormSubmission {
  id: string
  formId: string
  formVersion: number
  /** Captured values keyed by FormField.key. The workflow dimensions contract. */
  values: Record<string, unknown>
  attachments: FormAttachmentRef[]
  context: WorkflowContextRef
  status: 'submitted' | 'draft'
  /** Post-approval SAP deploy state (default status 'none'). */
  deploy?: FormSubmissionDeploy
  /** Current request status; null until one is set (the form's initial status applies). */
  requestStatus?: FormSubmissionRequestStatus | null
  /** Governed request number (FormSpec.numbering), set by the server at
   *  submission. Null on older and unnumbered requests: use
   *  requestReference() to show either. */
  requestNumber?: string | null
  /** Set on a record imported from another system (Admin > Data Import): its
   *  original record number and source. Historical records are read-only and
   *  never start a workflow. Null on requests made here. */
  historical?: FormSubmissionHistorical | null
  createdBy: string | null
  createdAt: string
  /** Marked as controlled (CUI / FOUO / export-controlled), migration 00535. */
  cui?: boolean
  /** How it was marked: the mapped field, the whole form, or by hand. */
  cuiSource?: 'field' | 'form' | 'manual' | null
}

/** Where an imported historical record came from. */
export interface FormSubmissionHistorical {
  /** The record number in the old system, e.g. an Ideagen record number. */
  legacyReference: string
  sourceSystem: string
  batchId: string | null
  meta: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

let _idSeq = 0
function nextId(prefix: string): string {
  _idSeq += 1
  return `${prefix}_${Date.now().toString(36)}_${_idSeq}`
}
export const newFieldId = (): string => nextId('fld')
export const newSectionId = (): string => nextId('sec')
export const newViewId = (): string => nextId('view')
export const newOptionId = (): string => nextId('opt')

/** Slugify a label into a stable, unique machine key within a form. */
export function slugifyKey(label: string, taken: Set<string>): string {
  const base = (label || 'field')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'field'
  if (!taken.has(base)) return base
  let i = 2
  while (taken.has(`${base}_${i}`)) i += 1
  return `${base}_${i}`
}

export function emptyFormSpec(name = 'Untitled form'): FormSpec {
  const sectionId = newSectionId()
  const viewId = newViewId()
  return {
    name,
    description: '',
    submitLabel: 'Submit',
    fields: [],
    sections: [{ id: sectionId, title: 'Details', columns: 1, fieldIds: [] }],
    views: [{ id: viewId, title: 'Page 1', sectionIds: [sectionId] }],
    wizard: false,
    contextBinding: 'global',
    notes: '',
  }
}

/** A new field of a given type with sensible per-type defaults. */
export function newField(type: FormFieldType, label?: string, taken: Set<string> = new Set()): FormField {
  const lbl = label ?? DEFAULT_FIELD_LABEL[type]
  const field: FormField = {
    id: newFieldId(),
    type,
    key: slugifyKey(lbl, taken),
    label: lbl,
    width: 'full',
  }
  if (type === 'currency') { field.currencyCode = 'USD'; field.decimals = 2 }
  if (type === 'percent') { field.min = 0; field.max = 100; field.decimals = 0 }
  if (type === 'single_select' || type === 'multi_select') {
    field.options = [
      { id: newOptionId(), label: 'Option 1', value: 'option_1' },
      { id: newOptionId(), label: 'Option 2', value: 'option_2' },
    ]
  }
  if (type === 'file') { field.maxFiles = 1; field.maxSizeMb = 10 }
  if (type === 'lookup') { field.entity = 'project' }
  if (type === 'document_repository') { field.repository = { libraryId: null, scope: 'library' } }
  if (type === 'document_reference') { field.documentRef = { releasedOnly: true } }
  return field
}

export const DEFAULT_FIELD_LABEL: Record<FormFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  rich_text: 'Rich text',
  number: 'Number',
  currency: 'Amount',
  percent: 'Percent',
  date: 'Date',
  datetime: 'Date & time',
  boolean: 'Yes / No',
  single_select: 'Choose one',
  multi_select: 'Choose many',
  email: 'Email',
  phone: 'Phone',
  url: 'Website',
  file: 'Attachment',
  signature: 'Signature',
  section_header: 'Section',
  lookup: 'Lookup',
  calculated: 'Calculated',
  sap_lookup: 'SAP lookup',
  people: 'User / Group',
  document_repository: 'Documents',
  document_reference: 'Reference document',
}

// ---------------------------------------------------------------------------
// Defensive normalization helpers (mirror workflow-spec.ts)
// ---------------------------------------------------------------------------

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}
function asOptString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}
function asBoolOrUndef(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined
}
function asNumberOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return undefined
}
function asStringMap(v: unknown): Record<string, string> | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const out: Record<string, string> = {}
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) if (typeof val === 'string' && val) out[k] = val
  return Object.keys(out).length ? out : undefined
}
function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out = v.filter((x): x is string => typeof x === 'string')
  return out.length ? out : undefined
}
function normalizeFieldType(v: unknown): FormFieldType {
  const t = asString(v) as FormFieldType
  return FORM_FIELD_TYPES.includes(t) ? t : 'short_text'
}
function normalizeWidth(v: unknown): FormFieldWidth | undefined {
  const w = asString(v)
  return w === 'full' || w === 'half' || w === 'third' ? w : undefined
}
function normalizeColumns(v: unknown): SectionColumns | undefined {
  const n = asNumberOrUndef(v)
  return n === 1 || n === 2 || n === 3 ? n : undefined
}

function normalizeOptions(v: unknown): FormFieldOption[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out = v.map((raw, i) => {
    const o = (raw ?? {}) as Record<string, unknown>
    const label = asString(o.label) || `Option ${i + 1}`
    const parentValues = normalizeParentValues(o.parentValues)
    return {
      id: asString(o.id) || newOptionId(),
      label,
      value: asString(o.value) || label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `option_${i + 1}`,
      ...(parentValues ? { parentValues } : {}),
    }
  })
  return out.length ? out : undefined
}

/** Unique, non-blank parent values; undefined when none. Never throws. */
export function normalizeParentValues(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out = [...new Set(v.map((x) => (typeof x === 'string' ? x.trim() : typeof x === 'number' ? String(x) : '')).filter(Boolean))]
  return out.length ? out : undefined
}

/** Allowed-option rules with a usable condition and a value list; junk dropped. */
function normalizeAllowedOptionsRules(v: unknown): AllowedOptionsRule[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out: AllowedOptionsRule[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const when = Array.isArray(r.when)
      ? r.when.map(normalizeVisibilityRule).filter((x): x is VisibilityRule => !!x)
      : normalizeVisibility(r.when)
    const hasCondition = Array.isArray(when) ? when.length > 0 : Boolean(when)
    const values = Array.isArray(r.values)
      ? [...new Set(r.values.map((x) => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : '')).filter(Boolean))]
      : []
    if (!hasCondition || !when) continue
    out.push({ when, values })
  }
  return out.length ? out : undefined
}

/** Normalize the numbering block: trimmed text, blank entries dropped. */
export function normalizeNumbering(raw: unknown): FormNumberingConfig | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const n = raw as Record<string, unknown>
  const pattern = typeof n.pattern === 'string' ? n.pattern.trim() : ''
  const sitePatterns: Record<string, string> = {}
  if (n.sitePatterns && typeof n.sitePatterns === 'object' && !Array.isArray(n.sitePatterns)) {
    for (const [code, p] of Object.entries(n.sitePatterns as Record<string, unknown>)) {
      const c = code.trim().toUpperCase()
      if (c && typeof p === 'string' && p.trim()) sitePatterns[c] = p.trim()
    }
  }
  if (!pattern && Object.keys(sitePatterns).length === 0) return undefined
  return { ...(pattern ? { pattern } : {}), ...(Object.keys(sitePatterns).length ? { sitePatterns } : {}) }
}

function normalizeVisibilityRule(raw: unknown): VisibilityRule | null {
  const r = (raw ?? {}) as Record<string, unknown>
  const fieldKey = asString(r.fieldKey)
  const op = asString(r.op) as VisibilityOp
  if (!fieldKey || !VISIBILITY_OPS.includes(op)) return null
  return { fieldKey, op, value: r.value }
}

/** Normalize one compiled data-standard rule; null when the shape is unusable.
 *  Defensive (AI-emitted input): coerces numbers, drops junk `when` entries,
 *  and requires the kind-specific payload to be present. */
export function normalizeStandardRule(raw: unknown): CompiledStandardRule | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const kind = asString(r.kind) as StandardRuleKind
  if (!STANDARD_RULE_KINDS.includes(kind)) return null
  const whenRaw = Array.isArray(r.when) ? r.when : []
  const when = whenRaw.map(normalizeVisibilityRule).filter((x): x is VisibilityRule => !!x)
  const rule: CompiledStandardRule = { kind }
  if (when.length) rule.when = when
  const message = asOptString(r.message)
  if (message) rule.message = message
  switch (kind) {
    case 'prefix':
    case 'suffix': {
      const value = typeof r.value === 'string' ? r.value : typeof r.value === 'number' ? String(r.value) : ''
      if (!value) return null
      rule.value = value
      return rule
    }
    case 'length': {
      rule.min = asNumberOrUndef(r.min)
      rule.max = asNumberOrUndef(r.max)
      rule.exact = asNumberOrUndef(r.exact)
      if (rule.min == null && rule.max == null && rule.exact == null) return null
      return rule
    }
    case 'charset': {
      const charset = asString(r.charset) as StandardCharset
      if (!STANDARD_CHARSETS.includes(charset)) return null
      rule.charset = charset
      return rule
    }
    case 'regex': {
      const pattern = asOptString(r.pattern)
      if (!pattern) return null
      rule.pattern = pattern
      return rule
    }
    case 'in_list': {
      const values = Array.isArray(r.values)
        ? r.values.map((v) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')).filter(Boolean)
        : []
      if (values.length === 0) return null
      rule.values = values
      return rule
    }
    default:
      return null
  }
}

/** Normalize a field's dataStandard blob defensively; undefined when absent or
 *  contentless. Preserves the author text and uninterpretable statements even
 *  when every rule was dropped, so nothing is silently lost. */
function normalizeDataStandard(raw: unknown): FieldDataStandard | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const s = raw as Record<string, unknown>
  const text = asString(s.text)
  const rulesRaw = Array.isArray(s.rules) ? s.rules : []
  const rules = rulesRaw.map(normalizeStandardRule).filter((x): x is CompiledStandardRule => !!x)
  const uninterpretable = asStringArray(s.uninterpretable)
  if (!text.trim() && rules.length === 0 && !uninterpretable) return undefined
  return {
    text,
    rules,
    ...(uninterpretable ? { uninterpretable } : {}),
    compiledAt: asString(s.compiledAt) || new Date().toISOString(),
    ...(asOptString(s.compiledFrom) !== undefined ? { compiledFrom: asOptString(s.compiledFrom) } : {}),
  }
}
function normalizeVisibility(raw: unknown): VisibilityGuard | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const g = raw as Record<string, unknown>
  const all = Array.isArray(g.all) ? g.all.map(normalizeVisibilityRule).filter((x): x is VisibilityRule => !!x) : undefined
  const any = Array.isArray(g.any) ? g.any.map(normalizeVisibilityRule).filter((x): x is VisibilityRule => !!x) : undefined
  if ((all && all.length) || (any && any.length)) {
    return { ...(all && all.length ? { all } : {}), ...(any && any.length ? { any } : {}) }
  }
  return undefined
}

function normalizeField(raw: unknown, idx: number, takenKeys: Set<string>): FormField {
  const s = (raw ?? {}) as Record<string, unknown>
  const type = normalizeFieldType(s.type)
  const label = asString(s.label) || DEFAULT_FIELD_LABEL[type] || `Field ${idx + 1}`
  // Preserve a provided key when present + unique; else slugify from label.
  let key = asString(s.key).toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
  if (!key || takenKeys.has(key)) key = slugifyKey(label, takenKeys)
  takenKeys.add(key)

  const field: FormField = {
    id: asString(s.id) || newFieldId(),
    type,
    key,
    label,
    helpText: asOptString(s.helpText),
    placeholder: asOptString(s.placeholder),
    required: asBoolOrUndef(s.required),
    archived: asBoolOrUndef(s.archived),
    defaultValue: s.defaultValue,
    dateSource: (type === 'date' || type === 'datetime') && s.dateSource === 'first_workflow_submission' ? 'first_workflow_submission' : undefined,
    width: normalizeWidth(s.width),
    visibleWhen: normalizeVisibility(s.visibleWhen),
    requiredWhen: normalizeVisibility(s.requiredWhen),
    minLength: asNumberOrUndef(s.minLength),
    maxLength: asNumberOrUndef(s.maxLength),
    regex: asOptString(s.regex),
    regexMessage: asOptString(s.regexMessage),
    rows: asNumberOrUndef(s.rows),
    dataStandard: normalizeDataStandard(s.dataStandard),
    min: asNumberOrUndef(s.min),
    max: asNumberOrUndef(s.max),
    step: asNumberOrUndef(s.step),
    integerOnly: asBoolOrUndef(s.integerOnly),
    currencyCode: asOptString(s.currencyCode),
    decimals: asNumberOrUndef(s.decimals),
    minDate: asOptString(s.minDate)?.trim() || undefined,
    maxDate: asOptString(s.maxDate)?.trim() || undefined,
    conditionalDateRules: (type === 'date' || type === 'datetime') && Array.isArray(s.conditionalDateRules)
      ? s.conditionalDateRules.map(raw => {
        const rule = raw as Record<string, unknown>
        return { when: Array.isArray(rule.when) ? rule.when.map(normalizeVisibilityRule).filter((r): r is VisibilityRule => !!r) : normalizeVisibility(rule.when) ?? {},
          minDate: asOptString(rule.minDate)?.trim() || undefined, maxDate: asOptString(rule.maxDate)?.trim() || undefined, message: asOptString(rule.message) }
      }) : undefined,
    trueLabel: asOptString(s.trueLabel),
    falseLabel: asOptString(s.falseLabel),
    options: normalizeOptions(s.options),
    // Only a select carries a list reference; '' (shared, not picked yet) is kept.
    optionListId: (type === 'single_select' || type === 'multi_select') && typeof s.optionListId === 'string' ? s.optionListId : undefined,
    allowOther: asBoolOrUndef(s.allowOther),
    dependsOn: (type === 'single_select' || type === 'multi_select') ? asOptString(s.dependsOn)?.trim() || undefined : undefined,
    allowedOptionsWhen: (type === 'single_select' || type === 'multi_select') ? normalizeAllowedOptionsRules(s.allowedOptionsWhen) : undefined,
    minSelected: asNumberOrUndef(s.minSelected),
    maxSelected: asNumberOrUndef(s.maxSelected),
    allowedSchemes: asStringArray(s.allowedSchemes),
    country: asOptString(s.country),
    maxFiles: asNumberOrUndef(s.maxFiles),
    maxSizeMb: asNumberOrUndef(s.maxSizeMb),
    acceptedTypes: asStringArray(s.acceptedTypes),
    requireTypedName: asBoolOrUndef(s.requireTypedName),
    subtitle: asOptString(s.subtitle),
    entity: asOptString(s.entity) as WorkflowContextType | undefined,
    multiple: asBoolOrUndef(s.multiple),
    formula: asOptString(s.formula),
    sapDdicRef: asOptString(s.sapDdicRef),
    sapSource: asOptString(s.sapSource),
    sapProjectId: asOptString(s.sapProjectId),
    sapFilters: asStringMap(s.sapFilters),
    sapScopeFieldKey: asOptString(s.sapScopeFieldKey),
    sourceFieldKey: asOptString(s.sourceFieldKey),
    sourceAttribute: asOptString(s.sourceAttribute),
    editableInWorkflow: asBoolOrUndef(s.editableInWorkflow),
    // Only a repository element carries the block; on any other type it is dropped.
    repository: type === 'document_repository' ? normalizeRepository(s.repository) : undefined,
    // Only a people field reads from the live directory; 'staff' is the default and not stored.
    peopleSource: type === 'people' && s.peopleSource === 'directory' ? 'directory' : undefined,
    prefillFrom: REQUESTER_ATTRIBUTES.some((a) => a.value === s.prefillFrom) ? (s.prefillFrom as RequesterAttribute) : undefined,
    documentRef: type === 'document_reference' ? normalizeDocumentRef(s.documentRef) : undefined,
  }
  // Strip undefined keys so the persisted spec stays lean.
  return JSON.parse(JSON.stringify(field)) as FormField
}

function normalizeSection(raw: unknown, idx: number, fieldIdSet: Set<string>): FormSection {
  const s = (raw ?? {}) as Record<string, unknown>
  const fieldIds = Array.isArray(s.fieldIds)
    ? (s.fieldIds as unknown[]).map((x) => asString(x)).filter((id) => id && fieldIdSet.has(id))
    : []
  return {
    id: asString(s.id) || newSectionId(),
    title: asString(s.title) || `Section ${idx + 1}`,
    description: asOptString(s.description),
    columns: normalizeColumns(s.columns),
    repeatable: asBoolOrUndef(s.repeatable),
    minRows: asNumberOrUndef(s.minRows),
    maxRows: asNumberOrUndef(s.maxRows),
    addRowLabel: asOptString(s.addRowLabel),
    fieldIds,
  }
}

function normalizeView(raw: unknown, idx: number, sectionIdSet: Set<string>): FormView {
  const s = (raw ?? {}) as Record<string, unknown>
  const sectionIds = Array.isArray(s.sectionIds)
    ? (s.sectionIds as unknown[]).map((x) => asString(x)).filter((id) => id && sectionIdSet.has(id))
    : []
  return {
    id: asString(s.id) || newViewId(),
    title: asString(s.title) || `Page ${idx + 1}`,
    description: asOptString(s.description),
    sectionIds,
  }
}

function normalizeContextBinding(v: unknown): FormSurface {
  const t = asString(v) as FormSurface
  return FORM_SURFACES.some((s) => s.value === t) ? t : 'global'
}

/**
 * Normalize the bulk config defensively. Returns undefined (bulk simply off)
 * whenever the blob is missing, disabled, or points at a section that does not
 * exist on this spec, so a stale/garbage `bulk` can never break a form. Never
 * throws. `maxRows` is clamped to 1..BULK_MAX_ROWS_CEILING.
 */
function normalizeBulk(raw: unknown, sections: FormSection[], fieldKeys: Set<string>): FormBulkConfig | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const b = raw as Record<string, unknown>
  if (b.enabled !== true) return undefined
  const sectionId = asString(b.sectionId)
  const section = sections.find((s) => s.id === sectionId)
  if (!section) return undefined
  const rawMax = asNumberOrUndef(b.maxRows)
  const maxRows = rawMax == null ? undefined : Math.max(1, Math.min(BULK_MAX_ROWS_CEILING, Math.floor(rawMax)))
  const objectKeyFieldKey = asOptString(b.objectKeyFieldKey)
  return {
    enabled: true,
    sectionId: section.id,
    ...(maxRows != null ? { maxRows } : {}),
    ...(objectKeyFieldKey && fieldKeys.has(objectKeyFieldKey) ? { objectKeyFieldKey } : {}),
  }
}

// ---------------------------------------------------------------------------
// Public validator/normalizer
// ---------------------------------------------------------------------------

/** Reject malformed date rules instead of quietly dropping their constraints. */
export function dateRuleConfigErrors(raw: Record<string, unknown>): string[] {
  const errors: string[] = []
  const label = typeof raw.label === 'string' ? raw.label : 'Date field'
  const hasConditional = raw.conditionalDateRules != null
  if (hasConditional && !['date', 'datetime'].includes(String(raw.type))) return [`${label}: conditional date rules require a Date or Date & time field.`]
  if (!['date', 'datetime'].includes(String(raw.type))) return errors
  const checkBounds = (item: Record<string, unknown>, context: string) => {
    for (const key of ['minDate', 'maxDate']) {
      if (item[key] != null && (typeof item[key] !== 'string' || (String(item[key]).trim() !== '' && !parseUtcDate(item[key])))) errors.push(`${label}: ${context}${key} must be an ISO date/time, today, or today+Nd / today-Nd (up to 36500 days).`)
    }
    const lower = parseUtcDate(item.minDate), upper = parseUtcDate(item.maxDate)
    if (lower && upper && lower.ms > upper.ms + (upper.dayOnly ? 86_399_999 : 0)) errors.push(`${label}: ${context}earliest date is after latest date.`)
  }
  checkBounds(raw, '')
  if (!hasConditional) return errors
  if (!Array.isArray(raw.conditionalDateRules)) return [...errors, `${label}: conditionalDateRules must be an array.`]
  for (const [index, value] of raw.conditionalDateRules.entries()) {
    const context = `rule ${index + 1}: `
    if (!value || typeof value !== 'object' || Array.isArray(value)) { errors.push(`${label}: ${context}must be an object.`); continue }
    const item = value as Record<string, unknown>
    checkBounds(item, context)
    if (!item.minDate && !item.maxDate) errors.push(`${label}: ${context}set an earliest or latest date.`)
    if (item.message !== undefined && typeof item.message !== 'string') errors.push(`${label}: ${context}message must be text.`)
    const guard = item.when
    const parts = Array.isArray(guard) ? [guard] : guard && typeof guard === 'object'
      ? [(guard as Record<string, unknown>).all, (guard as Record<string, unknown>).any].filter(part => part !== undefined) : []
    if (!parts.length || parts.some(part => !Array.isArray(part)) || !parts.some(part => Array.isArray(part) && part.length > 0)) {
      errors.push(`${label}: ${context}add a condition using when: [...] or when: {all: [...], any: [...]}.`); continue
    }
    for (const condition of parts.flat() as unknown[]) {
      const rule = condition as Record<string, unknown> | null
      if (!rule || typeof rule !== 'object' || typeof rule.fieldKey !== 'string' || !rule.fieldKey.trim() || !VISIBILITY_OPS.includes(rule.op as VisibilityOp)) errors.push(`${label}: ${context}each condition needs a fieldKey and a supported comparison.`)
    }
  }
  return errors
}

/** Defensive normalizer: unique keys and resolved field/section/view references. */
export function validateFormSpec(
  input: unknown,
): { ok: true; spec: FormSpec } | { ok: false; errors: string[] } {
  const errors: string[] = []
  if (!input || typeof input !== 'object') {
    return { ok: false, errors: ['Form spec is not an object.'] }
  }
  const raw = input as Record<string, unknown>
  const name = asString(raw.name).trim()
  if (!name) errors.push('Form needs a name.')
  if (errors.length > 0) return { ok: false, errors }

  // Fields (uniqueness enforced by the shared takenKeys set).
  const takenKeys = new Set<string>()
  const fieldsRaw = Array.isArray(raw.fields) ? raw.fields : []
  for (const field of fieldsRaw) if (field && typeof field === 'object') errors.push(...dateRuleConfigErrors(field as Record<string, unknown>))
  if (errors.length) return { ok: false, errors }
  const fields = fieldsRaw.map((f, i) => normalizeField(f, i, takenKeys))
  const fieldIdSet = new Set(fields.map((f) => f.id))

  // Sections; drop dangling field refs.
  const sectionsRaw = Array.isArray(raw.sections) ? raw.sections : []
  let sections = sectionsRaw.map((s, i) => normalizeSection(s, i, fieldIdSet))

  // Collect orphan fields (not referenced by any section) into a default section.
  const placed = new Set<string>(sections.flatMap((s) => s.fieldIds))
  const orphans = fields.filter((f) => !placed.has(f.id)).map((f) => f.id)
  if (orphans.length > 0) {
    sections = [...sections, { id: newSectionId(), title: 'Details', columns: 1, fieldIds: orphans }]
  }
  if (sections.length === 0) {
    sections = [{ id: newSectionId(), title: 'Details', columns: 1, fieldIds: fields.map((f) => f.id) }]
  }
  const sectionIdSet = new Set(sections.map((s) => s.id))

  // Views; drop dangling section refs; ensure every section is on some view.
  const viewsRaw = Array.isArray(raw.views) ? raw.views : []
  let views = viewsRaw.map((v, i) => normalizeView(v, i, sectionIdSet))
  const shownSections = new Set<string>(views.flatMap((v) => v.sectionIds))
  const orphanSections = sections.filter((s) => !shownSections.has(s.id)).map((s) => s.id)
  if (views.length === 0) {
    views = [{ id: newViewId(), title: 'Page 1', sectionIds: sections.map((s) => s.id) }]
  } else if (orphanSections.length > 0) {
    const first = views[0]
    if (first) first.sectionIds = [...first.sectionIds, ...orphanSections]
  }

  const approvalRaw = (raw.approval ?? null) as Record<string, unknown> | null
  const approval: FormApprovalConfig | undefined = approvalRaw && approvalRaw.enabled
    ? {
        enabled: true,
        approverRole: asOptString(approvalRaw.approverRole) ?? null,
        slaHours: asNumberOrUndef(approvalRaw.slaHours) ?? null,
      }
    : undefined

  const spec: FormSpec = {
    name,
    description: asOptString(raw.description),
    submitLabel: asOptString(raw.submitLabel) ?? 'Submit',
    fields,
    sections,
    views,
    bulk: normalizeBulk(raw.bulk, sections, new Set(fields.map((f) => f.key))),
    wizard: asBoolOrUndef(raw.wizard),
    contextBinding: normalizeContextBinding(raw.contextBinding),
    surfaceVisibility: normalizeVisibility(raw.surfaceVisibility),
    approval,
    workflowDraftId: asOptString(raw.workflowDraftId),
    postApproval: normalizePostApproval(raw.postApproval),
    publicPage: normalizePublicPage(raw.publicPage),
    requestStatuses: normalizeRequestStatuses(raw.requestStatuses),
    numbering: normalizeNumbering(raw.numbering),
    notes: asOptString(raw.notes),
  }
  return { ok: true, spec }
}

/**
 * Normalize a form's request statuses: known keys only, unique machine keys,
 * exactly one initial status (the first when none or several are marked).
 * Returns undefined for an empty list, so forms without statuses stay without
 * the key. Never throws.
 */
export function normalizeRequestStatuses(raw: unknown): RequestStatus[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const seen = new Set<string>()
  const out: RequestStatus[] = []
  for (const item of raw.slice(0, 40)) {
    if (!item || typeof item !== 'object') continue
    const r = item as Record<string, unknown>
    const label = typeof r.label === 'string' ? r.label.trim().slice(0, 60) : ''
    if (!label) continue
    let key = typeof r.key === 'string' && r.key.trim() ? r.key.trim() : requestStatusKey(label)
    key = key.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'status'
    while (seen.has(key)) key = `${key}_2`
    seen.add(key)
    const color = REQUEST_STATUS_COLORS.includes(r.color as RequestStatusColor) ? (r.color as RequestStatusColor) : 'slate'
    const n = r.notify && typeof r.notify === 'object' ? (r.notify as Record<string, unknown>) : null
    const recipients = n && Array.isArray(n.recipients)
      ? [...new Set(n.recipients.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean))].slice(0, 50)
      : []
    const notify: RequestStatusNotify = {
      ...(n?.requester === true ? { requester: true } : {}),
      ...(recipients.length ? { recipients } : {}),
      ...(n?.email === true ? { email: true } : {}),
      ...(n?.teamsDirect === true ? { teamsDirect: true } : {}),
      ...(typeof n?.teamsChannel === 'string' && n.teamsChannel.trim() ? { teamsChannel: n.teamsChannel.trim().slice(0, 120) } : {}),
      ...(typeof n?.message === 'string' && n.message.trim() ? { message: n.message.trim().slice(0, 1000) } : {}),
    }
    out.push({
      key,
      label,
      color,
      ...(r.initial === true ? { initial: true } : {}),
      ...(r.closed === true ? { closed: true } : {}),
      ...(Object.keys(notify).length ? { notify } : {}),
    })
  }
  if (out.length === 0) return undefined
  const firstInitial = out.findIndex((s) => s.initial)
  out.forEach((s, i) => {
    if (i === (firstInitial >= 0 ? firstInitial : 0)) s.initial = true
    else delete s.initial
  })
  return out
}

/** A machine key for a new status label: "In Review" -> "in_review". */
export function requestStatusKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'status'
}

/**
 * Normalize the public page settings defensively: only the five known keys,
 * only strings, each cut to its limit, blank ones dropped. Returns undefined
 * when nothing is set, so a form without settings stays without the key. The
 * text is kept as typed: whether a logo address or an email is acceptable is
 * decided where it is read (readPublicPage), and the builder shows that same
 * verdict next to the input. Never throws.
 */
export function normalizePublicPage(raw: unknown): FormPublicPage | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const p = raw as Record<string, unknown>
  const out: FormPublicPage = {}
  for (const key of Object.keys(PUBLIC_PAGE_LIMITS) as Array<keyof FormPublicPage>) {
    const v = p[key]
    if (typeof v !== 'string' || v.trim() === '') continue
    out[key] = v.slice(0, PUBLIC_PAGE_LIMITS[key])
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** Normalize a post-approval SAP activity binding (drops if disabled/incomplete). */
function normalizePostApproval(raw: unknown): SapActivityBinding | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const p = raw as Record<string, unknown>
  const activityId = asString(p.activityId)
  if (!p.enabled || !activityId) return undefined
  const fieldMapRaw = p.fieldMap && typeof p.fieldMap === 'object' ? (p.fieldMap as Record<string, unknown>) : {}
  const fieldMap: Record<string, string> = {}
  for (const [k, v] of Object.entries(fieldMapRaw)) if (typeof v === 'string' && v) fieldMap[k] = v
  return {
    enabled: true,
    activityId,
    agentWorkstream: asOptString(p.agentWorkstream) ?? null,
    fieldMap: Object.keys(fieldMap).length ? fieldMap : undefined,
  }
}

/** Convenience: index fields by id, then by key. */
export function fieldMaps(spec: FormSpec): { byId: Map<string, FormField>; byKey: Map<string, FormField> } {
  const byId = new Map<string, FormField>()
  const byKey = new Map<string, FormField>()
  for (const f of spec.fields) { byId.set(f.id, f); byKey.set(f.key, f) }
  return { byId, byKey }
}

/** True for fields that capture a submittable value (excludes headings and
 *  document repository elements). */
export function isValueField(f: FormField): boolean {
  return !PRESENTATIONAL_FIELD_TYPES.includes(f.type)
}

// ---------------------------------------------------------------------------
// Bulk (multi-object) helpers
// ---------------------------------------------------------------------------

/** True when this form can capture many objects in one request. */
export function isBulkForm(spec: FormSpec): boolean {
  return Boolean(spec.bulk?.enabled && spec.sections.some((s) => s.id === spec.bulk!.sectionId))
}

/** Effective row cap for a bulk form. */
export function bulkMaxRows(spec: FormSpec): number {
  return spec.bulk?.maxRows ?? DEFAULT_BULK_MAX_ROWS
}

/** The section that supplies the per-row columns, or undefined. */
export function bulkSection(spec: FormSpec): FormSection | undefined {
  if (!spec.bulk?.enabled) return undefined
  return spec.sections.find((s) => s.id === spec.bulk!.sectionId)
}

/** The per-row (column) fields, in section order. Archived + presentational
 *  fields are excluded: they are neither captured nor exported. */
export function bulkRowFields(spec: FormSpec): FormField[] {
  const section = bulkSection(spec)
  if (!section) return []
  const { byId } = fieldMaps(spec)
  return section.fieldIds
    .map((id) => byId.get(id))
    .filter((f): f is FormField => !!f && isValueField(f) && !f.archived)
}

/** The header fields: everything captured once per request (not a row column). */
export function bulkHeaderFields(spec: FormSpec): FormField[] {
  const rowIds = new Set(bulkSection(spec)?.fieldIds ?? [])
  return spec.fields.filter((f) => !rowIds.has(f.id))
}

/** Read the captured rows for a bulk form. Always returns a new array; never
 *  throws on a malformed stored value. */
export function getBulkRows(spec: FormSpec, values: Record<string, unknown>): Record<string, unknown>[] {
  if (!spec.bulk?.enabled) return []
  const raw = values[spec.bulk.sectionId]
  if (!Array.isArray(raw)) return []
  return raw.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r))
}

/** Write rows back onto a values bag (does not mutate the input). */
export function withBulkRows(
  spec: FormSpec,
  values: Record<string, unknown>,
  rows: Record<string, unknown>[],
): Record<string, unknown> {
  if (!spec.bulk?.enabled) return values
  return { ...values, [spec.bulk.sectionId]: rows }
}

/** True when this submission actually carries bulk rows (Multiple mode). A
 *  bulk-enabled form submitted in Single mode has no rows and behaves exactly
 *  as it did before bulk existed. */
export function hasBulkRows(spec: FormSpec, values: Record<string, unknown>): boolean {
  return getBulkRows(spec, values).length > 0
}
